import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaStar, FaGift, FaShieldAlt, FaClock, FaMobile, FaHeadset, FaCheck, FaTimes, FaArrowLeft } from 'react-icons/fa';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import SEO from '../components/SEO';

const CasinoReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const casinos: Record<string, any> = {
    allbritishcasino: {
      name: 'All British Casino',
      image: '/images/offers/allbritishcasino.webp',
      rating: 4.8,
      bonus: '100% up to £100 + 10% Cashback',
      wagering: '35x',
      minDeposit: '£10',
      maxBet: '£5',
      payoutTime: '0-24 hours',
      license: 'UK Gambling Commission',
      established: '2013',
      games: 2000,
      providers: 50,
      pros: [
        'Fast withdrawal processing',
        'Excellent customer support',
        'Wide game selection',
        'Mobile-optimized platform',
        'Regular promotions'
      ],
      cons: [
        'Limited payment methods',
        'High wagering requirements'
      ],
      description: 'All British Casino stands out as one of the most trusted UK-facing casinos, offering a premium gaming experience with a focus on British players. With over a decade of operation, this casino has built a reputation for reliability, fast payouts, and exceptional customer service.',
      gameData: [
        { name: 'Slots', value: 65, color: '#0ea5e9' },
        { name: 'Table Games', value: 20, color: '#3b82f6' },
        { name: 'Live Casino', value: 10, color: '#60a5fa' },
        { name: 'Jackpots', value: 5, color: '#93c5fd' },
      ],
      monthlyData: [
        { month: 'Jan', players: 45000, withdrawals: 42000 },
        { month: 'Feb', players: 48000, withdrawals: 45000 },
        { month: 'Mar', players: 52000, withdrawals: 49000 },
        { month: 'Apr', players: 55000, withdrawals: 52000 },
        { month: 'May', players: 58000, withdrawals: 55000 },
        { month: 'Jun', players: 60000, withdrawals: 57000 },
      ],
      ratingBreakdown: [
        { category: 'Games', score: 95 },
        { category: 'Bonuses', score: 90 },
        { category: 'Support', score: 98 },
        { category: 'Payouts', score: 97 },
        { category: 'Mobile', score: 94 },
        { category: 'Security', score: 99 },
      ],
    },
    casinocasino: {
      name: 'Casino Casino',
      image: '/images/offers/casinocasino.webp',
      rating: 4.7,
      bonus: '200% up to £200 + 50 Free Spins',
      wagering: '40x',
      minDeposit: '£20',
      maxBet: '£5',
      payoutTime: '1-48 hours',
      license: 'UK Gambling Commission',
      established: '2015',
      games: 2500,
      providers: 60,
      pros: [
        'Generous welcome bonus',
        'Extensive game library',
        'VIP rewards program',
        '24/7 customer support',
        'Multiple payment options'
      ],
      cons: [
        'Higher wagering requirements',
        'Some country restrictions'
      ],
      description: 'Casino Casino has established itself as a leading destination for UK players seeking variety and value. With an impressive game collection from top providers and a generous bonus structure, this casino delivers an exciting gaming experience backed by strong security measures.',
      gameData: [
        { name: 'Slots', value: 70, color: '#0ea5e9' },
        { name: 'Table Games', value: 15, color: '#3b82f6' },
        { name: 'Live Casino', value: 10, color: '#60a5fa' },
        { name: 'Jackpots', value: 5, color: '#93c5fd' },
      ],
      monthlyData: [
        { month: 'Jan', players: 52000, withdrawals: 48000 },
        { month: 'Feb', players: 55000, withdrawals: 51000 },
        { month: 'Mar', players: 58000, withdrawals: 54000 },
        { month: 'Apr', players: 60000, withdrawals: 56000 },
        { month: 'May', players: 62000, withdrawals: 58000 },
        { month: 'Jun', players: 65000, withdrawals: 61000 },
      ],
      ratingBreakdown: [
        { category: 'Games', score: 97 },
        { category: 'Bonuses', score: 92 },
        { category: 'Support', score: 95 },
        { category: 'Payouts', score: 94 },
        { category: 'Mobile', score: 96 },
        { category: 'Security', score: 98 },
      ],
    },
    pubcasino: {
      name: 'Pub Casino',
      image: '/images/offers/pubcasino.webp',
      rating: 4.6,
      bonus: '150% up to £150 + 25 Free Spins',
      wagering: '30x',
      minDeposit: '£10',
      maxBet: '£5',
      payoutTime: '0-48 hours',
      license: 'UK Gambling Commission',
      established: '2014',
      games: 1800,
      providers: 45,
      pros: [
        'Lower wagering requirements',
        'Sports betting available',
        'Regular tournaments',
        'Cashback offers',
        'User-friendly interface'
      ],
      cons: [
        'Smaller game selection',
        'Limited live dealer options'
      ],
      description: 'Pub Casino combines the best of casino gaming and sports betting in one comprehensive platform. With competitive bonuses, lower wagering requirements, and a friendly interface, this casino appeals to both casual players and serious gamblers looking for value.',
      gameData: [
        { name: 'Slots', value: 60, color: '#0ea5e9' },
        { name: 'Table Games', value: 25, color: '#3b82f6' },
        { name: 'Live Casino', value: 10, color: '#60a5fa' },
        { name: 'Sports', value: 5, color: '#93c5fd' },
      ],
      monthlyData: [
        { month: 'Jan', players: 38000, withdrawals: 35000 },
        { month: 'Feb', players: 40000, withdrawals: 37000 },
        { month: 'Mar', players: 42000, withdrawals: 39000 },
        { month: 'Apr', players: 45000, withdrawals: 42000 },
        { month: 'May', players: 48000, withdrawals: 45000 },
        { month: 'Jun', players: 50000, withdrawals: 47000 },
      ],
      ratingBreakdown: [
        { category: 'Games', score: 92 },
        { category: 'Bonuses', score: 88 },
        { category: 'Support', score: 93 },
        { category: 'Payouts', score: 91 },
        { category: 'Mobile', score: 90 },
        { category: 'Security', score: 97 },
      ],
    },
  };

  const casino = casinos[id || ''];

  if (!casino) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Casino Not Found</h1>
          <Link to="/" className="text-quantum-400 hover:underline">Return to Home</Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Organization',
      name: casino.name,
    },
    author: {
      '@type': 'Organization',
      name: 'JackpotQuantum.com',
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: casino.rating,
      bestRating: 5,
    },
    reviewBody: casino.description,
  };

  return (
    <>
      <SEO
        title={`${casino.name} Review - ${casino.rating}/5 Rating`}
        description={`Complete review of ${casino.name}. ${casino.bonus} welcome bonus, ${casino.games}+ games, ${casino.payoutTime} payouts. Read our expert analysis.`}
        canonical={`/review/${id}`}
        jsonLd={jsonLd}
        ogImage={casino.image}
      />

      <div className="min-h-screen bg-dark-900">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-dark-800 to-dark-900">
          <div className="container mx-auto px-4">
            <Link
              to="/"
              className="inline-flex items-center space-x-2 text-quantum-400 hover:text-quantum-300 mb-6 transition-colors"
            >
              <FaArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>

            <div className="glass rounded-2xl p-8 md:p-12">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0">
                  <img
                    src={casino.image}
                    alt={casino.name}
                    className="w-full md:w-64 h-48 md:h-64 object-cover rounded-xl"
                    loading="eager"
                  />
                </div>
                <div className="flex-grow">
                  <div className="flex items-center space-x-4 mb-4">
                    <h1 className="text-4xl md:text-5xl font-bold text-white">{casino.name}</h1>
                    <div className="flex items-center space-x-1 bg-dark-700 px-4 py-2 rounded-full">
                      <FaStar className="w-5 h-5 text-yellow-400" />
                      <span className="text-white font-bold text-xl">{casino.rating}</span>
                    </div>
                  </div>
                  <p className="text-gray-300 text-lg mb-6">{casino.description}</p>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center space-x-2">
                      <FaGift className="w-5 h-5 text-quantum-400" />
                      <span className="text-white font-semibold">{casino.bonus}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaClock className="w-5 h-5 text-quantum-400" />
                      <span className="text-gray-300">Payout: {casino.payoutTime}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Stats */}
        <section className="py-12 bg-dark-800">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Games', value: casino.games.toLocaleString() + '+' },
                { label: 'Providers', value: casino.providers },
                { label: 'Established', value: casino.established },
                { label: 'License', value: 'UKGC' },
              ].map((stat, i) => (
                <div key={i} className="glass rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-quantum-400 mb-2">{stat.value}</div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Rating Breakdown */}
        <section className="py-20 bg-dark-900">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              <span className="text-gradient">Detailed Ratings</span>
            </h2>
            <div className="glass rounded-2xl p-8 mb-8">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={casino.ratingBreakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" domain={[0, 100]} stroke="#9CA3AF" />
                  <YAxis dataKey="category" type="category" stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#F3F4F6' }}
                  />
                  <Bar dataKey="score" fill="#0ea5e9" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* Game Distribution */}
        <section className="py-20 bg-dark-800">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              <span className="text-gradient">Game Distribution</span>
            </h2>
            <div className="glass rounded-2xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={casino.gameData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${percent ? (percent * 100).toFixed(0) : 0}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {casino.gameData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#F3F4F6' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col justify-center space-y-4">
                  {casino.gameData.map((game: any, i: number) => (
                    <div key={i} className="flex items-center space-x-4">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: game.color }}></div>
                      <div className="flex-grow">
                        <div className="flex justify-between mb-1">
                          <span className="text-white font-medium">{game.name}</span>
                          <span className="text-gray-400">{game.value}%</span>
                        </div>
                        <div className="w-full bg-dark-700 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-1000"
                            style={{ width: `${game.value}%`, backgroundColor: game.color }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Monthly Performance */}
        <section className="py-20 bg-dark-900">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              <span className="text-gradient">Monthly Performance</span>
            </h2>
            <div className="glass rounded-2xl p-8">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={casino.monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#F3F4F6' }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="players" stroke="#0ea5e9" strokeWidth={3} name="Active Players" />
                  <Line type="monotone" dataKey="withdrawals" stroke="#3b82f6" strokeWidth={3} name="Successful Withdrawals" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* Pros and Cons */}
        <section className="py-20 bg-dark-800">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="glass rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
                  <FaCheck className="w-6 h-6 text-green-400" />
                  <span>Pros</span>
                </h3>
                <ul className="space-y-3">
                  {casino.pros.map((pro: string, i: number) => (
                    <li key={i} className="flex items-start space-x-3">
                      <FaCheck className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                      <span className="text-gray-300">{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="glass rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
                  <FaTimes className="w-6 h-6 text-red-400" />
                  <span>Cons</span>
                </h3>
                <ul className="space-y-3">
                  {casino.cons.map((con: string, i: number) => (
                    <li key={i} className="flex items-start space-x-3">
                      <FaTimes className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                      <span className="text-gray-300">{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Bonus Details */}
        <section className="py-20 bg-dark-900">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              <span className="text-gradient">Bonus Information</span>
            </h2>
            <div className="glass rounded-2xl p-8 max-w-3xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { label: 'Welcome Bonus', value: casino.bonus },
                  { label: 'Wagering Requirements', value: casino.wagering },
                  { label: 'Minimum Deposit', value: casino.minDeposit },
                  { label: 'Maximum Bet', value: casino.maxBet },
                  { label: 'Payout Time', value: casino.payoutTime },
                  { label: 'License', value: casino.license },
                ].map((item, i) => (
                  <div key={i} className="border-b border-dark-700 pb-4">
                    <div className="text-gray-400 text-sm mb-1">{item.label}</div>
                    <div className="text-white font-semibold text-lg">{item.value}</div>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-6 bg-quantum-500/10 rounded-xl border border-quantum-500/20">
                <p className="text-gray-300 text-sm">
                  <strong className="text-white">Important:</strong> Always read the full terms and conditions before claiming any bonus. 
                  Wagering requirements must be met before withdrawals can be made. Play responsibly.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-quantum-900/20 to-dark-900">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Play at {casino.name}?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Claim your welcome bonus and start playing today. New players only. 18+ | BeGambleAware.org
            </p>
            <button className="bg-gradient-to-r from-quantum-500 to-quantum-600 text-white py-4 px-8 rounded-xl font-bold text-lg hover:from-quantum-600 hover:to-quantum-700 transition-all transform hover:scale-105 shadow-lg shadow-quantum-500/30">
              Visit {casino.name}
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

export default CasinoReview;

